import { hasSupabaseConfig, supabase } from '../../../shared/api/supabaseClient.js';

const REALTIME_API_URL = 'https://realtime.doroparty.com';
const PLATFORM_CHAT_UNION_PAGE_LIMIT = 20;
const PLATFORM_CHAT_UNION_MAX_PAGES = 100;
const ERROR_MESSAGES = {
  authentication_required: '로그인 세션을 확인할 수 없습니다. 다시 로그인해주세요.',
  platform_admin_required: '플랫폼 관리자만 유니온 채팅을 검수할 수 있습니다.',
  union_out_of_scope: '담당하는 커뮤니티 유형(갤러리/채널)의 유니온만 검수할 수 있습니다.',
  union_required: '검수할 유니온을 선택해주세요.',
  union_not_found: '선택한 유니온을 찾을 수 없습니다.',
  message_required: '처리할 메시지를 선택해주세요.',
  message_not_found: '처리할 메시지를 찾을 수 없습니다.',
  moderation_action_invalid: '지원하지 않는 채팅 관리 동작입니다.',
  moderation_reason_invalid: '처리 사유는 500자 이하로 입력해주세요.',
  message_already_hidden: '이미 숨김 처리된 메시지입니다.',
  message_not_hidden: '현재 숨김 상태가 아닌 메시지입니다.',
  season_required: '삭제할 레이드 차수를 선택해주세요.',
  season_chat_empty: '이 차수에는 삭제할 메시지가 없습니다.',
};

function client() {
  if (!hasSupabaseConfig || !supabase) throw new Error('Supabase 환경 변수가 설정되지 않았습니다.');
  return supabase;
}

function moderationError(error, fallback) {
  const raw = String(error?.message || error || '');
  const code = Object.keys(ERROR_MESSAGES).find((candidate) => raw.includes(candidate));
  const next = new Error(ERROR_MESSAGES[code] || raw || fallback || '유니온 채팅 관리 요청을 처리하지 못했습니다.');
  next.code = code || error?.code || 'unknown_error';
  return next;
}

async function realtimeAdminRequest(path, body) {
  const sessionResult = await client().auth.getSession();
  const accessToken = String(sessionResult.data?.session?.access_token || '');
  if (sessionResult.error || !accessToken) throw moderationError(new Error('authentication_required'));
  const response = await fetch(`${REALTIME_API_URL}${path}`, {
    method: 'POST',
    headers: {
      'content-type': 'application/json',
      authorization: `Bearer ${accessToken}`,
    },
    body: JSON.stringify(body || {}),
  });
  let payload = null;
  try { payload = await response.json(); } catch { payload = null; }
  if (!response.ok) throw moderationError(new Error(String(payload?.error || `realtime_admin_failed_${response.status}`)));
  return payload || {};
}

function normalizeSeason(row = {}) {
  return {
    seasonId: String(row.season_id ?? row.seasonId ?? ''),
    seasonNo: Number(row.season_no ?? row.seasonNo ?? 0),
    status: String(row.status || ''),
    messageCount: Number(row.message_count ?? row.messageCount ?? 0),
    hiddenCount: Number(row.hidden_count ?? row.hiddenCount ?? 0),
    lastMessageAt: String(row.last_message_at ?? row.lastMessageAt ?? ''),
  };
}

function normalizeUnion(row = {}) {
  return {
    unionId: String(row.union_id || ''),
    unionName: String(row.union_name || '유니온'),
    guildId: String(row.guild_id || ''),
    registrationStatus: String(row.registration_status || ''),
    communityType: String(row.community_type || ''),
    seasons: (Array.isArray(row.seasons) ? row.seasons : []).map(normalizeSeason),
  };
}

function normalizeTimelineUnion(row = {}) {
  if (!row || typeof row !== 'object') return null;
  const unionId = String(row.union_id ?? row.unionId ?? '');
  const unionName = String(row.union_name ?? row.unionName ?? row.name ?? '');
  const guildId = String(row.guild_id ?? row.guildId ?? '');
  if (!unionId && !unionName && !guildId) return null;
  return {
    unionId,
    unionName: unionName || '유니온',
    guildId,
  };
}

function normalizeTimelineSeason(row = {}) {
  if (!row || typeof row !== 'object') return null;
  const seasonId = String(row.season_id ?? row.seasonId ?? '');
  if (!seasonId) return null;
  return normalizeSeason(row);
}

function normalizeModeration(row) {
  if (!row || typeof row !== 'object') return null;
  return {
    id: String(row.id || ''),
    action: String(row.action || ''),
    reason: String(row.reason || ''),
    actorUserId: String(row.actor_user_id ?? row.actorUserId ?? ''),
    actorLabel: String(row.actor_label ?? row.actorLabel ?? '플랫폼 관리자'),
    createdAt: String(row.created_at ?? row.createdAt ?? ''),
  };
}

function normalizeMessage(row = {}) {
  const eventData = row.event_data && typeof row.event_data === 'object' ? row.event_data : {};
  return {
    id: String(row.id || ''),
    unionId: String(row.union_id ?? row.unionId ?? ''),
    seasonId: String(row.season_id ?? row.seasonId ?? ''),
    authorUserId: String(row.author_user_id ?? row.authorUserId ?? ''),
    authorNickname: String(row.author_nickname ?? row.authorNickname ?? '멤버'),
    messageType: String(row.message_type ?? row.type ?? 'text'),
    body: String(row.body || ''),
    replyToId: String(row.reply_to_id ?? row.replyToId ?? ''),
    orderId: String(row.order_id ?? row.orderId ?? ''),
    mentionedUserIds: (Array.isArray(row.mentioned_user_ids) ? row.mentioned_user_ids : []).map(String),
    nikkeRefs: Array.isArray(row.nikke_refs) ? row.nikke_refs.slice(0, 5) : [],
    eventData,
    sourceRecordKey: String(row.source_record_key ?? eventData.record_key ?? ''),
    createdAt: String(row.created_at ?? row.createdAt ?? ''),
    deletedAt: String(row.deleted_at ?? row.deletedAt ?? ''),
    moderation: normalizeModeration(row.moderation),
  };
}

export async function loadPlatformChatUnions() {
  const unions = [];
  let offset = 0;
  for (let page = 0; page < PLATFORM_CHAT_UNION_MAX_PAGES; page += 1) {
    const realtime = await realtimeAdminRequest('/v1/admin/chat/unions', {
      limit: PLATFORM_CHAT_UNION_PAGE_LIMIT,
      offset,
    });
    const pageUnions = Array.isArray(realtime?.unions) ? realtime.unions : [];
    unions.push(...pageUnions);
    const pagination = realtime?.pagination && typeof realtime.pagination === 'object' ? realtime.pagination : {};
    const hasMore = Boolean(pagination.has_more ?? pagination.hasMore);
    if (!hasMore) break;
    const nextOffset = Number(pagination.next_offset ?? pagination.nextOffset ?? offset + pageUnions.length);
    offset = Number.isFinite(nextOffset) && nextOffset > offset
      ? Math.floor(nextOffset)
      : offset + Math.max(pageUnions.length, PLATFORM_CHAT_UNION_PAGE_LIMIT);
  }
  return unions.map(normalizeUnion);
}

export async function loadPlatformUnionChatMessages({
  unionId,
  seasonId = '',
  union = null,
  season = null,
  limit = 200,
  includeHidden = true,
}) {
  const normalizedUnion = normalizeTimelineUnion(union) || (unionId ? {
    unionId: String(unionId),
    unionName: '유니온',
    guildId: '',
  } : null);
  const normalizedSeason = normalizeTimelineSeason(season);

  const resolvedSeasonId = String(normalizedSeason?.seasonId || seasonId || '');
  if (!resolvedSeasonId) {
    return {
      union: normalizedUnion,
      season: null,
      messages: [],
    };
  }

  const realtime = await realtimeAdminRequest('/v1/admin/chat/messages', {
    unionId,
    seasonId: resolvedSeasonId,
    limit: Number(limit) || 200,
    includeHidden: Boolean(includeHidden),
  });

  return {
    union: normalizedUnion,
    season: normalizedSeason || normalizeTimelineSeason({ season_id: resolvedSeasonId }),
    messages: (Array.isArray(realtime?.messages) ? realtime.messages : []).map(normalizeMessage),
  };
}

export async function moderatePlatformUnionChatMessage({ unionId, seasonId, messageId, action, reason }) {
  try {
    return await realtimeAdminRequest('/v1/admin/chat/moderate', {
      unionId,
      seasonId,
      messageId,
      action,
      reason: String(reason || '').trim(),
    });
  } catch (error) {
    throw moderationError(error, action === 'restore' ? '메시지 복원에 실패했습니다.' : '메시지 숨김에 실패했습니다.');
  }
}

export async function purgePlatformUnionChatSeason({ unionId, seasonId, reason }) {
  try {
    return await realtimeAdminRequest('/v1/admin/chat/purge-season', {
      unionId,
      seasonId,
      reason: String(reason || '').trim(),
    });
  } catch (error) {
    throw moderationError(error, '차수 채팅 전체 삭제에 실패했습니다.');
  }
}
